import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Phone,
  Mail,
  ChevronRight,
  Check,
  Plus,
  Layers,
  Settings,
  ExternalLink,
  FileText,
  Edit3,
  DollarSign,
  Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import syncService from '../services/syncService';
import { getJobberAuthUrl } from '../services/jobberConfig';
import { JobberService } from '../services/jobberService';
import PublicHeader from './PublicHeader';
import TypewriterText from './TypewriterText';
import './OnboardingPage.css';

const CUSTOMER_TYPES = ['Residential', 'Commercial', 'Storefront', 'Construction'];
const DEFAULT_WINDOW_TYPES = [
  { name: 'Standard Pane', price: 10 },
  { name: 'XL Pane', price: 20 },
  { name: 'French Pane', price: 3.5 },
  { name: 'Screens', price: 5 },
  { name: 'Skylight', price: 15 }
];

const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const getPhoneDigits = (formatted) => formatted.replace(/\D/g, '');

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { currentUser, loginAnonymously } = useAuth();

  const [step, setStep] = useState(1);
  const [businessName, setBusinessName] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [selectedCustomerTypes, setSelectedCustomerTypes] = useState([]);
  const [primaryCustomerType, setPrimaryCustomerType] = useState('');
  const [otherCustomerType, setOtherCustomerType] = useState('');
  const [windowTypes, setWindowTypes] = useState(
    DEFAULT_WINDOW_TYPES.map((wt, i) => ({
      id: `wt_${i}`,
      name: wt.name,
      price: wt.price,
      category: 'exterior_interior'
    }))
  );
  const [customWindowType, setCustomWindowType] = useState('');
  const [connectingJobber, setConnectingJobber] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const totalSteps = 7;
  const featureSteps = [
    { title: 'Add New Quote', desc: 'Tap the New Quote button to create quotes for your customers.' },
    { title: 'Add Structures', desc: 'Add multiple buildings or areas to a single quote.' },
    { title: 'Edit Price Per Window', desc: 'Swipe left on a window type in the quote to override the price.' },
    { title: 'Extra Dirty Fee', desc: 'Add an extra dirty fee when windows need deeper cleaning.' },
    { title: 'Quote Menu', desc: 'Send quote to client as PDF or push directly to Jobber.' },
    { title: 'Custom Pricing', desc: 'In Settings, add window types to customer types with custom pricing.' }
  ];

  // For testing: sign in anonymously when landing on onboarding without a user
  useEffect(() => {
    if (!currentUser) {
      loginAnonymously()
        .then(() => {})
        .catch((err) => console.error('Anonymous login failed:', err));
    }
  }, [currentUser, loginAnonymously]);

  useEffect(() => {
    document.title = `Squeekit - Setup (${step}/${totalSteps})`;
  }, [step]);

  const toggleCustomerType = (type) => {
    if (type === 'Other') {
      if (selectedCustomerTypes.includes('Other')) {
        setSelectedCustomerTypes((prev) => prev.filter((t) => t !== 'Other'));
        setOtherCustomerType('');
      } else {
        setSelectedCustomerTypes((prev) => [...prev, 'Other']);
      }
    } else {
      setSelectedCustomerTypes((prev) =>
        prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
      );
    }
  };

  const addCustomWindowType = () => {
    const name = customWindowType.trim() || 'Other';
    if (!windowTypes.some((w) => w.name === name)) {
      setWindowTypes((prev) => [
        ...prev,
        { id: `wt_${Date.now()}`, name, price: 10, category: 'exterior_interior' }
      ]);
      setCustomWindowType('');
    }
  };

  const updateWindowPrice = (id, price) => {
    const num = parseFloat(price) || 0;
    setWindowTypes((prev) =>
      prev.map((w) => (w.id === id ? { ...w, price: num } : w))
    );
  };

  const deleteWindowType = (id) => {
    if (windowTypes.length <= 1) return;
    setWindowTypes((prev) => prev.filter((w) => w.id !== id));
  };

  const canProceed = () => {
    if (step === 1) return businessName.trim() && businessEmail.trim();
    if (step === 2) return selectedCustomerTypes.length > 0 && primaryCustomerType;
    if (step === 3) return windowTypes.length > 0;
    return true;
  };

  const handleNext = () => {
    if (step === 7) {
      handleComplete();
    } else {
      setStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleComplete = async () => {
    if (!currentUser) return;
    setIsCompleting(true);

    try {
      const customerTypesFinal = selectedCustomerTypes.map((name) => {
        const displayName = (name === 'Other' ? (otherCustomerType || 'Other') : name);
        const windowPrices = {};
        windowTypes.forEach((wt) => {
          windowPrices[wt.name] = wt.price;
        });
        return {
          id: crypto.randomUUID?.(),
          name: displayName,
          windowPrices,
          windowPricesOrder: windowTypes.map((w) => w.name)
        };
      });

      const windowTypesFinal = windowTypes.map((wt) => ({
        id: wt.id || crypto.randomUUID?.(),
        name: wt.name,
        price: Number(wt.price) || 0,
        category: wt.category || 'exterior_interior'
      }));

      await syncService.saveUserSettings(
        { windowTypes: windowTypesFinal, customerTypes: customerTypesFinal },
        currentUser.uid
      );

      const profileRef = doc(db, 'users', currentUser.uid, 'profile', 'data');
      await setDoc(
        profileRef,
        {
          businessName: businessName.trim(),
          phoneNumber: getPhoneDigits(businessPhone),
          email: businessEmail.trim(),
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
      localStorage.setItem(
        `userProfile_${currentUser.uid}`,
        JSON.stringify({
          businessName: businessName.trim(),
          phoneNumber: getPhoneDigits(businessPhone),
          address: ''
        })
      );

      localStorage.setItem(`onboarding_completed_${currentUser.uid}`, 'true');
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Onboarding complete error:', err);
      alert('Could not save your setup. Please try again.');
    } finally {
      setIsCompleting(false);
    }
  };

  const handleConnectJobber = async () => {
    if (!currentUser) return;
    setConnectingJobber(true);
    const authUrl = getJobberAuthUrl(currentUser.uid);
    const popup = window.open(authUrl, 'jobber-oauth', 'width=600,height=700,scrollbars=yes,resizable=yes');
    if (!popup) {
      setConnectingJobber(false);
      alert('Popup blocked. Please allow popups and try again.');
      return;
    }
    const checkClosed = setInterval(async () => {
      if (popup.closed) {
        clearInterval(checkClosed);
        setConnectingJobber(false);
        await new Promise((r) => setTimeout(r, 500));
        await JobberService.loadTokensForUser(currentUser.uid);
        if (JobberService.isAuthenticated()) {
          setStep(7);
        }
      }
    }, 500);
  };

  return (
    <div className="onboarding">
      <PublicHeader />
      <div className="onboarding__progress">
        <div
          className="onboarding__progress-fill"
          style={{ width: `${(step / totalSteps) * 100}%` }}
        />
      </div>

      <div className="onboarding__content">
        {/* Step 1: Business info */}
        {step === 1 && (
          <div className="onboarding__step onboarding__step--fade">
            <h2 className="onboarding__title">Tell us about your business</h2>
            <p className="onboarding__subtitle">We use this for your quotes and profile.</p>
            <div className="onboarding__form">
              <div className="onboarding__field">
                <label>
                  <Building2 className="onboarding__icon" />
                  Business Name
                </label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="the best window cleaners"
                />
              </div>
              <div className="onboarding__field">
                <label>
                  <Phone className="onboarding__icon" />
                  Phone
                </label>
                <input
                  type="tel"
                  value={businessPhone}
                  onChange={(e) => setBusinessPhone(formatPhoneNumber(e.target.value))}
                  placeholder="(555) 123-4567"
                  maxLength={14}
                />
              </div>
              <div className="onboarding__field">
                <label>
                  <Mail className="onboarding__icon" />
                  Email
                </label>
                <input
                  type="email"
                  value={businessEmail}
                  onChange={(e) => setBusinessEmail(e.target.value)}
                  placeholder="your_email@soapy.com"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Customer types */}
        {step === 2 && (
          <div className="onboarding__step onboarding__step--fade">
            <h2 className="onboarding__title onboarding__title--center">What type of customers do you service?</h2>
            <p className="onboarding__subtitle onboarding__subtitle--center">Select the type of Customers you serve</p>
            <div className="onboarding__chips">
              {CUSTOMER_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`onboarding__chip ${selectedCustomerTypes.includes(type) ? 'onboarding__chip--selected' : ''}`}
                  onClick={() => toggleCustomerType(type)}
                >
                  {selectedCustomerTypes.includes(type) && <Check className="onboarding__chip-check" />}
                  {type}
                </button>
              ))}
              <button
                type="button"
                className={`onboarding__chip ${selectedCustomerTypes.includes('Other') ? 'onboarding__chip--selected' : ''}`}
                onClick={() => toggleCustomerType('Other')}
              >
                {selectedCustomerTypes.includes('Other') && <Check className="onboarding__chip-check" />}
                Other
              </button>
            </div>
            {selectedCustomerTypes.includes('Other') && (
              <div className="onboarding__field onboarding__field--inline">
                <input
                  type="text"
                  value={otherCustomerType}
                  onChange={(e) => setOtherCustomerType(e.target.value)}
                  placeholder="Enter your customer type"
                />
              </div>
            )}
            {selectedCustomerTypes.length > 0 && (
              <>
                <p className="onboarding__label onboarding__label--spaced onboarding__label--large">Which do you create quotes for most often?</p>
                <div className="onboarding__chips onboarding__chips--primary">
                  {selectedCustomerTypes.map((type) => {
                    const displayName = type === 'Other' ? (otherCustomerType || 'Other') : type;
                    const isPrimary = primaryCustomerType === displayName;
                    return (
                      <button
                        key={type}
                        type="button"
                        className={`onboarding__chip onboarding__chip--primary ${isPrimary ? 'onboarding__chip--selected' : ''}`}
                        onClick={() => setPrimaryCustomerType(displayName)}
                      >
                        {isPrimary && <Check className="onboarding__chip-check" />}
                        {displayName}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 3: Window types & pricing */}
        {step === 3 && (
          <div className="onboarding__step onboarding__step--fade">
            <h2 className="onboarding__title onboarding__title--center">Set your window pricing</h2>
            <TypewriterText
              text={`Awesome, I bet you kill it in the ${primaryCustomerType || 'Residential'} window market. Lets keep it that way. What is your standard pricing for this customer type? You can always change it later :)`}
              tag="p"
              className="onboarding__subtitle onboarding__subtitle--align-tiles"
              reserveSpace
            />
            <div className="onboarding__window-section">
              <div className="onboarding__window-list">
                {windowTypes.map((wt) => (
                  <div
                    key={wt.id}
                    className="onboarding__window-row"
                  >
                    <span className="onboarding__window-name">{wt.name}</span>
                    <div className="onboarding__window-right">
                      <div className="onboarding__window-price">
                        <span className="onboarding__dollar">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={wt.price || ''}
                          onChange={(e) => updateWindowPrice(wt.id, e.target.value)}
                          className="onboarding__window-price-input"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteWindowType(wt.id)}
                        className="onboarding__window-delete"
                        title="Delete window type"
                      >
                        <Trash2 className="onboarding__window-delete-icon" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="onboarding__add-window">
              <input
                type="text"
                value={customWindowType}
                onChange={(e) => setCustomWindowType(e.target.value)}
                placeholder="super crazy window name"
                onKeyDown={(e) => e.key === 'Enter' && addCustomWindowType()}
              />
              <button type="button" onClick={addCustomWindowType} className="onboarding__add-btn">
                <Plus className="onboarding__add-icon" />
                Add
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Quote preview */}
        {step === 4 && (
          <div className="onboarding__step onboarding__step--fade">
            <TypewriterText
              text={`You can now create quotes for ${primaryCustomerType || 'your primary customer type'} customers with your customized price per window type.`}
              tag="p"
              className="onboarding__animation-msg onboarding__animation-msg--top"
              boldRange={[29, 29 + (primaryCustomerType || 'your primary customer type').length + ' customers'.length]}
              reserveSpace
            />
            <div className="onboarding__quote-preview onboarding__quote-preview--compact">
              <div className="onboarding__quote-preview-header">
                <div className="onboarding__quote-preview-customer">
                  {businessName || 'Your Business Name'}
                </div>
                <div className="onboarding__quote-preview-address">123 Easy Street</div>
              </div>
              <h2 className="section-title onboarding__preview-title">Quote Preview</h2>
              <div className="onboarding__preview-table-wrap">
                {(() => {
                  const price = (name) => windowTypes.find((w) => w.name === name)?.price ?? 10;
                  const std = price('Standard Pane');
                  const screens = price('Screens');
                  const skylight = price('Skylight');
                  const mainTotal = 45 * std + 15 * screens;
                  const guestTotal = 12 * std + 3 * screens + 3 * skylight;
                  const exteriorTotal = (45 * std * 0.6) + (12 * std * 0.6);
                  const interiorTotal = (45 * std * 0.4) + (12 * std * 0.4);
                  const screensTotal = 15 * screens + 3 * screens;
                  const skylightTotal = 3 * skylight;
                  const grandTotal = mainTotal + guestTotal;
                  const lineItems = [
                    { structure: 'Main House', rows: [
                      { type: 'Standard Pane', qty: 45, price: std, total: 45 * std },
                      { type: 'Screens', qty: 15, price: screens, total: 15 * screens }
                    ]},
                    { structure: 'Guest House', rows: [
                      { type: 'Standard Pane', qty: 12, price: std, total: 12 * std },
                      { type: 'Screens', qty: 3, price: screens, total: 3 * screens },
                      { type: 'Skylight', qty: 3, price: skylight, total: 3 * skylight }
                    ]}
                  ];
                  return (
                    <>
                      {lineItems.map((st) => (
                        <div key={st.structure} className="onboarding__preview-structure onboarding__preview-structure--compact">
                          <div className="onboarding__preview-structure-header">
                            <span>{st.structure}</span>
                            <span>Total: ${st.rows.reduce((s, r) => s + r.total, 0).toFixed(2)}</span>
                          </div>
                          <div className="onboarding__preview-line-header">
                            <span>Window Type</span>
                            <span>Qty</span>
                            <span>Price</span>
                            <span>Total</span>
                          </div>
                          <div className="onboarding__preview-line-items">
                            {st.rows.map((r) => (
                              <div key={r.type} className="onboarding__preview-line-item">
                                <span>{r.type}</span>
                                <span>{r.qty}</span>
                                <span>${r.price.toFixed(2)}</span>
                                <span>${r.total.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      <div className="onboarding__preview-summary">
                        <div className="onboarding__preview-summary-row">
                          <span>Exterior Total</span>
                          <span>${exteriorTotal.toFixed(2)}</span>
                        </div>
                        <div className="onboarding__preview-summary-row">
                          <span>Interior Total</span>
                          <span>${interiorTotal.toFixed(2)}</span>
                        </div>
                        <div className="onboarding__preview-summary-row">
                          <span>Screens Total</span>
                          <span>${screensTotal.toFixed(2)}</span>
                        </div>
                        <div className="onboarding__preview-summary-row">
                          <span>Skylight Total</span>
                          <span>${skylightTotal.toFixed(2)}</span>
                        </div>
                        <div className="onboarding__preview-summary-row onboarding__preview-summary-row--total">
                          <span>Total</span>
                          <span>${grandTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Settings info */}
        {step === 5 && (
          <div className="onboarding__step onboarding__step--fade">
            <h2 className="onboarding__title">You are all set</h2>
            <p className="onboarding__subtitle">
              Go to Settings anytime to configure additional window types, customer types, and
              specific pricing per customer type.
            </p>
            <div className="onboarding__settings-preview">
              <Settings className="onboarding__settings-icon" />
              <p>Settings</p>
            </div>
          </div>
        )}

        {/* Step 6: Jobber */}
        {step === 6 && (
          <div className="onboarding__step onboarding__step--fade onboarding__step--center">
            <h2 className="onboarding__title">Connect to Jobber?</h2>
            <p className="onboarding__subtitle">
              Sync quotes directly to Jobber for seamless workflow.
            </p>
            <div className="onboarding__jobber-actions">
              <button
                type="button"
                className="onboarding__btn onboarding__btn--primary"
                onClick={handleConnectJobber}
                disabled={connectingJobber}
              >
                <ExternalLink className="onboarding__btn-icon" />
                {connectingJobber ? 'Connecting...' : 'Connect Jobber'}
              </button>
              <button
                type="button"
                className="onboarding__btn onboarding__btn--secondary"
                onClick={() => setStep(7)}
              >
                Skip for now
              </button>
            </div>
          </div>
        )}

        {/* Step 7: Feature walkthrough */}
        {step === 7 && (
          <div className="onboarding__step onboarding__step--fade">
            <h2 className="onboarding__title">Quick feature tour</h2>
            <p className="onboarding__subtitle">All the ways Squeekit helps you quote faster</p>
            <div className="onboarding__feature-grid">
              {featureSteps.map((f, idx) => (
                <div
                  key={idx}
                  className="onboarding__feature-card"
                  style={{ animationDelay: `${idx * 0.2}s` }}
                >
                  <div className="onboarding__feature-highlight">
                    {idx === 0 && <Plus />}
                    {idx === 1 && <Layers />}
                    {idx === 2 && <Edit3 />}
                    {idx === 3 && <DollarSign />}
                    {idx === 4 && <FileText />}
                    {idx === 5 && <Settings />}
                    <span>{f.title}</span>
                  </div>
                  <p className="onboarding__feature-card-desc">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="onboarding__footer">
        {step > 1 && (
          <button type="button" className="onboarding__btn onboarding__btn--ghost" onClick={handleBack}>
            Back
          </button>
        )}
        <div className="onboarding__footer-spacer" />
        <button
          type="button"
          className="onboarding__btn onboarding__btn--primary"
          onClick={handleNext}
          disabled={!canProceed() || isCompleting}
        >
          {isCompleting ? (
            'Saving...'
          ) : step === 7 ? (
            'Get Started'
          ) : (
            <>
              Continue
              <ChevronRight className="onboarding__btn-arrow" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default OnboardingPage;
